import { useMutation } from '@apollo/client';
import { FC, useState } from 'react';
import {
  GET_ALL_CERTIFICATES_WITH_JOINT_DETAILS,
  UPDATE_MC1,
} from '../../../graph-queries/tar/certificate';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';
import { MediaUploadRow } from '../../organisms/MediaUpload/MediaUpload';
import { Mc1, MediaContext } from '../../../graph-queries/__generated__/graphql';
import { useSelectedAsset } from '../../../lib/util';
import classes from '../../organisms/CertificateAttachments/CertificateAttachments.module.scss';
import { useFileUpload } from '../../organisms/CertificateAttachments/CertificateAttachments.util';
import FileUploadButton from '../../organisms/CertificateAttachments/FileUploadButton';
import AttachmentsList from '../../organisms/CertificateAttachments/AttachmentsList';
import SaveCancelButtons from '../../organisms/CertificateAttachments/SaveCancelButtons';

const MC1Attachment: FC<{ mc1: Mc1; onClose: () => void }> = ({ mc1, onClose }) => {
  const { assetId = -1 } = useSelectedAsset() || {};
  const [toBeDeleted, setToBeDeleted] = useState<number[]>([]);
  const { sendSuccess, sendError } = useNoticeBar();
  const [updateMc1, { loading }] = useMutation(UPDATE_MC1, {
    refetchQueries: [{ query: GET_ALL_CERTIFICATES_WITH_JOINT_DETAILS, variables: { assetId } }],
    awaitRefetchQueries: true,
  });

  const { state, dispatch, uploadFile, retry } = useFileUpload(MediaContext.Mc1);

  const handleUploadSuccess = (generated: { blobLocation: string; url: string }, file: File) => {
    return updateMc1({
      variables: {
        input: {
          mc1Id: mc1.mc1Id,
          fileName: file.name,
          blobLocation: generated.blobLocation,
          mime: file.type,
        },
      },
    });
  };

  const onSaveClick = async () => {
    if (state.fileRows.length) {
      await Promise.all(state.fileRows.map((row) => uploadFile(row.file, handleUploadSuccess)))
        .then(() => {
          sendSuccess(
            `Your ${state.fileRows.length === 1 ? 'file has' : 'files have'} been uploaded successfully!`
          );
          dispatch({ type: 'reset' });
        })
        .catch(() => sendError('Upload failed. Remove invalid file to continue.'));
    }

    if (toBeDeleted.length) {
      await Promise.all(
        toBeDeleted.map((mc1Id) =>
          updateMc1({
            variables: {
              input: {
                mc1Id,
                mime: '',
                fileName: '',
                blobLocation: '',
              },
            },
          })
        )
      )
        .then(() => {
          sendSuccess(
            `Your ${toBeDeleted.length === 1 ? 'file has' : 'files have'} been removed successfully!`
          );
          setToBeDeleted([]);
        })
        .catch(() => sendError('Remove failed.'));
    }
  };

  const attachments = [
    {
      id: mc1.mc1Id,
      url: mc1.mediaUrl || '',
      fileName: mc1.fileName || '',
      mime: mc1.mime || '',
    },
  ];

  return (
    <div className={classes.root}>
      <div className="row">
        <div className="col-md-5">
          <FileUploadButton onFileSelect={(files) => dispatch({ type: 'init', fileList: files })} />
        </div>
        {!!state.fileRows.length && (
          <div className="col-md-12">
            <div>
              {state.fileRows.map((row) => (
                <MediaUploadRow
                  key={row.file.name}
                  row={row}
                  dispatch={dispatch}
                  onRetry={() => retry(row.file, handleUploadSuccess)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <AttachmentsList
        attachments={attachments}
        loading={loading}
        toBeDeleted={toBeDeleted}
        onToggleDelete={(id) =>
          setToBeDeleted((list) =>
            list.includes(id) ? list.filter((i) => i !== id) : [...list, id]
          )
        }
      />
      <SaveCancelButtons
        onSave={onSaveClick}
        onCancel={onClose}
        saveDisabled={!state.fileRows.length && !toBeDeleted.length}
      />
    </div>
  );
};

export default MC1Attachment;
